/**
 * <sticky-atc> — docs/10 §12, docs/04 §9.
 *
 * Three jobs, and the third is the one that matters beyond this element:
 *
 *   1. Reveal once the real buy box has scrolled out of view, hide when it
 *      comes back. Driven by an IntersectionObserver on the buy box itself,
 *      not a scroll threshold — a threshold has to be re-guessed for every
 *      viewport height and every product whose gallery is a different size.
 *   2. Scroll back to the gallery or the buy box, header-aware.
 *   3. PUBLISH ITS OWN HEIGHT as --sticky-atc-height.
 *
 * Job 3 is why N7 is fixed. docs/04 §9 lists four elements competing for the
 * bottom edge and none of them accounting for the others: the cookie banner
 * (z 9999) covers this bar (z 40) on every product page before consent, and
 * the scroll-to-top's `bottom: 86px` sits inside this bar's 106px band. Step
 * 4.15 built both on --sticky-atc-height and left it at 0; this sets it, so
 * they stack above the bar rather than over it.
 */
(function () {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`) || document;
      this.buyBox = this.section.querySelector('[data-buy-box]');

      this.bindScrollButtons();
      this.bindBuying();
      this.observe();

      // The bar's height changes with the band — the thumbnail and title are
      // hidden below 768 (docs/10 §12.2) — so it is measured, not assumed.
      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.variantUnsubscriber?.();
      this.publishHeight(0);
    }

    /**
     * THE BAR BUYS FROM HERE — merchant instruction, 2026-08-14.
     *
     * Two halves. The first mirrors the picker: `product-info.js` publishes
     * `variantChange` with the resolved variant every time an option is
     * touched, and this rewrites the bar's hidden id, its label and its
     * disabled state from that payload. The bar holds no opinion about which
     * variant is selected — it cannot disagree with the buy box, because it
     * is only ever repeating what the buy box was told.
     *
     * The second half — a quick-checkout button that added and then
     * navigated — is gone. See : it is Shopify's dynamic
     * checkout now, which needs no JavaScript from us.
     */
    bindBuying() {
      this.variantInput = this.querySelector('[data-sticky-variant-id]');
      this.addButton = this.querySelector('[data-sticky-add]');
      this.addText = this.querySelector('[data-sticky-add-text]');
      this.chooseButton = this.querySelector('[data-sticky-choose], .sticky-atc__cta--choose');
      this.form = this.querySelector('form');

      /*
       * BARE IDENTIFIERS, NOT `window.` — and this cost a bug before it
       * shipped. `PUB_SUB_EVENTS` is a top-level `const` in constants.js, and
       * in a classic script `const` creates a global LEXICAL binding, not a
       * property of `window`. So `window.PUB_SUB_EVENTS` is undefined while
       * bare `PUB_SUB_EVENTS` resolves fine, and a `window.`-guarded
       * subscription would have silently never run — the bar would look
       * correct on load and then never follow the picker.
       */
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (typeof subscribe === 'function' && events?.variantChange) {
        this.variantUnsubscriber = subscribe(events.variantChange, ({ data }) => {
          // Other product sections publish too — a related-products carousel
          // has its own <product-info>. Only this section's news is ours.
          if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
          this.applyVariant(data.variant);
          this.applyRenderedPrice(data.html);
        });
      }

      this.bindOptionRow();
    }

    /**
     * The in-bar option row drives the REAL picker.
     *
     * It does not resolve variants, does not know what combinations exist and
     * does not touch the hidden id. It finds the radio in <variant-selects>
     * whose value matches and clicks it, and everything after that is the
     * page's normal variant-change path. If the radio cannot be found the
     * button degrades to scrolling to the picker, which is where the customer
     * was being sent before this row existed.
     */
    bindOptionRow() {
      this.optionButtons = Array.from(this.querySelectorAll('[data-sticky-option-value]'));
      if (!this.optionButtons.length) return;
      this.optionPosition =
        Number(this.querySelector('[data-sticky-option-position]')?.dataset.stickyOptionPosition) || 0;

      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const { stickyOption: name, stickyOptionValue: value } = button.dataset;
          const picker = this.section.querySelector('variant-selects');
          const radio = picker
            ? Array.from(picker.querySelectorAll('input[type="radio"]')).find(
                (input) => input.dataset.optionName === name && input.value === value,
              )
            : null;

          if (!radio) {
            this.scrollTo(picker || this.buyBox, true);
            return;
          }
          if (radio.checked) return;

          /*
           * NOT `radio.click()`. Measured: clicking from the bar jumped the
           * page 1673px back up to the gallery — the exact behaviour the
           * merchant asked to be rid of, reintroduced from a second source.
           *
           * `.click()` on an input FOCUSES it, and focusing a control that is
           * several screens above the viewport makes the browser scroll it
           * into view. The media-gallery fix (`prepend` guard) had already
           * stopped Dawn's scroll; this was the browser's own, and no amount
           * of work inside the gallery would have touched it.
           *
           * Setting `checked` and dispatching a bubbling `change` reaches the
           * same delegated listener on <variant-selects> (global.js
           * `connectedCallback`) and never touches focus.
           */
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.markSelected(value);
        });
      });
    }

    /**
     * ⚠ THE BAR'S PRICE NEVER FOLLOWED THE COLOUR.
     *
     * sticky-atc.liquid says the row, "like the price and the button beside
     * it, updates from that publication" — but `applyVariant` only ever
     * rewrote the hidden id, the tiles and the button. Both price copies were
     * printed once at page load. Measured 2026-09-29 at 390 on
     * mono-sherry25: tap White in the bar and it read "Colour: White" beside
     * Black's Rs. 2,799, while the page above had moved to White's Rs. 3,199
     * — the price the customer is then charged. Unlike a product card, which
     * says "From", the bar renders the EXACT variant price, so this was a
     * wrong price stated as fact.
     *
     * Copied from the server's own rendering rather than formatted here: the
     * `html` in the payload is the section re-rendered for the new variant,
     * and this snippet is inside that section, so the response already holds
     * a bar with the right sale price, strike-through, "% off" and sold-out
     * class — produced by the same `snippets/price` the page uses. Formatting
     * money in JavaScript would be a second implementation of that snippet,
     * and the two would drift. The WRAPPER's class list is copied too, because
     * `price--on-sale` and `price--sold-out` live there, not inside it.
     *
     * Matched by position: the desktop copy, then the phone readout, which
     * the snippet renders in that order.
     *
     * @param {Document|null} html the section as re-rendered for the new variant
     */
    applyRenderedPrice(html) {
      const next = html?.querySelector('sticky-atc');
      if (!next) return;
      const SELECTOR = '.sticky-atc__text .price, .sticky-atc__price-mobile .price';
      const fresh = next.querySelectorAll(SELECTOR);
      this.querySelectorAll(SELECTOR).forEach((price, i) => {
        if (!fresh[i]) return;
        price.className = fresh[i].className;
        price.innerHTML = fresh[i].innerHTML;
      });
    }

    /** @param {string} value the option value now selected */
    markSelected(value) {
      this.optionButtons?.forEach((button) => {
        const on = button.dataset.stickyOptionValue === value;
        button.classList.toggle('is-selected', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // and name it — six tiles show which is ringed, not what it is called
      const label = this.querySelector('[data-sticky-option-selected]');
      if (label && value) label.textContent = value;
    }

    /** @param {object|null} variant the resolved variant, or null for an unbuyable combination */
    applyVariant(variant) {
      const strings = window.variantStrings || {};

      if (!variant) {
        // No such combination. This is the ONLY case the old bar was built
        // for, and now the only case that still sends the customer back.
        this.form?.setAttribute('hidden', '');
        this.chooseButton?.removeAttribute('hidden');
        this.publishHeight();
        return;
      }

      this.form?.removeAttribute('hidden');
      this.chooseButton?.setAttribute('hidden', '');

      if (this.variantInput) this.variantInput.value = variant.id;

      // Re-mark from the RESOLVED variant, not from the click: the picker can
      // move the selection itself (an unavailable combination resolves to a
      // neighbouring one), and the row has to show what actually happened.
      if (this.optionPosition) {
        this.markSelected(variant[`option${this.optionPosition}`]);
      }

      const available = variant.available !== false;
      [this.addButton].forEach((b) => {
        if (!b) return;
        b.disabled = !available;
      });
      if (this.addText) {
        this.addText.textContent = available
          ? strings.addToCart || this.addText.textContent
          : strings.soldOut || 'Sold out';
      }
      this.publishHeight();
    }

    /*
     * `quickCheckout()` IS GONE — merchant, 2026-08-17, §1.-48.
     *
     * It ran `/cart/add.js` and then sent the browser to `/checkout`, and a
     * Shopify checkout is always the whole cart — so the bar's second button
     * bought everything in the bag, not the product being looked at.
     * `{{ form | payment_button }}` replaced it in `sticky-atc.liquid`:
     * Shopify's dynamic checkout scopes the checkout to the single line in
     * that form and needs no JavaScript from us.
     *
     * Deleted rather than left unreferenced, for the same reason the card's
     * handler was: a method that still knows how to check out the whole cart
     * is one `addEventListener` away from doing it again.
     */

    observe() {
      if (!this.buyBox) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          // Visible buy box -> the bar is redundant. SCROLLED PAST -> show it.
          //
          // "Not intersecting" alone was the old test, and it is also true
          // while the buy box is still BELOW the fold — which on a phone is
          // the first screen of every product page, the gallery filling it.
          // Measured on live, 2026-09-29, fresh load at 390x844, no scroll:
          // the bar showed at y=583, 173px tall, over the bottom of the photo
          // and the product's name. It now waits until the buy box has gone
          // up past the top, which is what job 1 above always said.
          //
          // HOW: the observed area runs from the top of the screen DOWN
          // without end (a 100000px bottom margin), so "intersecting" means
          // "not yet scrolled past". Testing `boundingClientRect.top < 0`
          // against the plain viewport was the first try and failed a jump
          // back to the top: above -> below never crosses the viewport, so
          // the observer never fired and the bar stayed up over the gallery.
          // With one boundary, the top edge, every change of state crosses it.
          this.hidden = entries[0].isIntersecting;
          this.publishHeight();
        },
        { rootMargin: '0px 0px 100000px 0px' },
      );
      this.observer.observe(this.buyBox);
    }

    /**
     * Publish the space this bar occupies at the bottom of the screen.
     *
     * NOT `rect.height` — docs/54 §1.-34. The bar floats inset now, so the
     * ground it covers is its own height PLUS the gap beneath it, and every
     * other bottom-anchored element (the app bar, the cookie banner, the
     * scroll-to-top) stacks on this number. Publishing the height alone put
     * the app bar 12px too low and straight over the floating bar's shadow.
     *
     * `innerHeight - rect.top` is that distance measured directly, so it stays
     * correct if the inset changes in CSS and needs no knowledge of it here.
     * Clamped at 0 because a bar scrolled fully off the bottom yields a
     * negative, and a negative offset would drag the stack down off-screen.
     */
    publishHeight(force) {
      let h = force;
      if (h === undefined) {
        h = this.hidden ? 0 : Math.max(0, Math.round(window.innerHeight - this.getBoundingClientRect().top));
      }
      document.documentElement.style.setProperty('--sticky-atc-height', `${h}px`);
    }

    bindScrollButtons() {
      this.querySelector('[data-scroll-to-gallery]')?.addEventListener('click', () => {
        this.scrollTo(this.section.querySelector('[data-gallery]'));
      });
      this.querySelectorAll('[data-scroll-to-buy]').forEach((el) => {
        el.addEventListener('click', () => {
          /*
           * "CHOOSE OPTIONS" GOES TO THE OPTIONS — docs/54 §1.-35.
           *
           * It scrolled to `[data-buy-box]` and focused the first control
           * inside it, which is the Add to cart button. Measured on the
           * organiser at 1440: after the click the buy box was in view at
           * top 105, the variant picker was at **-81 — above the viewport** —
           * and focus had landed on `button.product-form__submit`. So a
           * button labelled "Choose options" scrolled past the options and
           * put the keyboard on the wrong control. The merchant reported it
           * as "it doesnt redirect to options correctly".
           *
           * The picker is the target now, with the buy box as the fallback
           * for a product that has none — and the fallback is the old
           * behaviour exactly, so a single-variant page is unchanged.
           */
          const picker = this.section.querySelector('variant-selects, .product-form__input, [data-variant-picker]');
          this.scrollTo(picker || this.buyBox, true);
        });
      });
    }

    /**
     * Header-aware, which is the whole of docs/10 §18.2 and docs/29 §3.2. The
     * reference hardcodes `top: 30px` on its sticky columns, so pinned
     * content sits behind the stuck 102px header. --header-height is measured
     * by component-header.js; this reads it rather than assuming a number.
     */
    scrollTo(target, focusFirst) {
      if (!target) return;
      const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      const top = window.pageYOffset + target.getBoundingClientRect().top - header - 20;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });

      // Scrolling alone moves the viewport but not the keyboard. Send focus
      // to the control the visitor asked for, or the trip is pointless for
      // anyone not using a mouse.
      if (!focusFirst) return;
      /*
       * A radio inside the picker beats a button, because the picker's own
       * controls ARE radios and `querySelector` on the old list would have
       * skipped past them to whatever button came first in the panel.
       * `:checked` first so focus lands on the current choice rather than on
       * the first colour in the row.
       */
      const focusable =
        target.querySelector('input[type=radio]:checked, select, input[type=radio]') ||
        target.querySelector('button, [href], input:not([type=hidden])');
      if (!focusable) return;
      const land = () => focusable.focus({ preventScroll: true });
      reduced ? land() : window.setTimeout(land, 500);
    }
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyAtc);
  }
})();
